import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FilterBar from "@/components/FilterBar";
import { money, num, fiscalMonthLabel, TIER_LABELS } from "@/lib/format";
import { Banknote, TrendingUp, Wallet, AlertTriangle, ArrowUpRight } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { Badge } from "@/components/ui/badge";

function buildParams(f) {
  const p = {};
  ["fiscal_year", "department_id", "user_id"].forEach((k) => { if (f[k]) p[k] = f[k]; });
  return p;
}

function Stat({ label, value, sub, icon: Icon, testid, onClick }) {
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={`border border-border bg-card p-6 animate-fade-up ${onClick ? "cursor-pointer transition-colors hover:border-brand hover:bg-secondary/40" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">{label}</div>
        <Icon className="h-4 w-4 text-brand" />
      </div>
      <div className="mt-4 font-mono text-3xl tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">{sub}{onClick && <ArrowUpRight className="h-3 w-3 opacity-60" />}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [fiscalStart, setFiscalStart] = useState(1);

  useEffect(() => { api.get("/settings/fiscal").then((r) => setFiscalStart(r.data.fiscal_year_start_month || 1)).catch(() => {}); }, []);

  useEffect(() => {
    api.get("/dashboard/summary", { params: buildParams(filters) }).then((r) => setData(r.data)).catch(() => {});
  }, [filters]);

  const t = data?.totals || {};
  const trend = (data?.trend || []).map((x) => ({ ...x, name: fiscalMonthLabel(x.month, fiscalStart) }));
  const tierData = Object.entries(data?.tier_distribution || {})
    .filter(([k]) => !["none"].includes(k))
    .map(([k, v]) => ({ name: TIER_LABELS[k] || k, value: v }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tighter">Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {user?.role === "super_admin" ? "Firm-wide" : user?.role === "manager" ? "Your department" : "Your"} banking & commission performance.
        </p>
      </div>

      <FilterBar filters={filters} setFilters={setFilters} showPeriod={false} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="kpi-banked" label="Net Banked" value={money(t.banked)} sub="View commission detail" icon={Banknote} onClick={() => navigate("/commission")} />
        <Stat testid="kpi-commission" label="Commission Payable" value={money(t.commission)} sub={`${t.payable_count || 0} earners qualify`} icon={TrendingUp} onClick={() => navigate("/commission")} />
        <Stat testid="kpi-carry" label="Carry-Over" value={money(t.carry_out)} sub="Shortfall to next quarter" icon={Wallet} onClick={() => navigate("/commission")} />
        <Stat testid="kpi-debtors" label="Debtor Alerts" value={num(data?.debtor_alerts || 0, 0)} sub="Entries aged > 90 days" icon={AlertTriangle} onClick={() => navigate("/commission")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg font-medium tracking-tight">Banking trend</h2>
            <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Net banked by fiscal month</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trend} margin={{ left: -10, right: 8 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--brand))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--brand))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 0, border: "1px solid hsl(var(--border))", fontFamily: "var(--font-mono)", fontSize: 12 }} />
              <Area type="monotone" dataKey="nett" stroke="hsl(var(--brand))" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6">
          <h2 className="font-heading text-lg font-medium tracking-tight mb-6">Tier distribution</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={tierData} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(var(--border))", fontSize: 12 }} />
              <Bar dataKey="value" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-border bg-card p-6">
        <h2 className="font-heading text-lg font-medium tracking-tight mb-4">Top earners</h2>
        <div className="space-y-2">
          {(data?.top_earners || []).filter((r) => r.total_banked > 0).map((r) => (
            <div key={r.user_id} className="flex items-center justify-between border border-border px-4 py-3 hover:bg-secondary transition-colors" data-testid={`top-earner-${r.user_id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Badge variant="outline" className="rounded-none text-[10px]">{r.title}</Badge>
                <span className="font-medium truncate">{r.name}</span>
                <span className="text-xs text-muted-foreground hidden sm:inline">{r.department_name}</span>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right hidden sm:block">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Banked</div>
                  <div className="font-mono text-sm">{money(r.total_banked)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Commission</div>
                  <div className="font-mono text-sm text-positive flex items-center gap-1 justify-end">
                    {money(r.total_commission)} <ArrowUpRight className="h-3 w-3" />
                  </div>
                </div>
              </div>
            </div>
          ))}
          {(!data?.top_earners || data.top_earners.every((r) => r.total_banked === 0)) && (
            <div className="text-sm text-muted-foreground py-8 text-center">No banking captured yet for this selection.</div>
          )}
        </div>
      </div>
    </div>
  );
}
